import { useCallback, useMemo, useState } from 'react';
import { useToast } from '@aimsos/ui';
import { useTranslation } from '@aimsos/i18n';
import DecisionFooter from '../../DecisionFooter.jsx';
import { DecisionConfirmModal } from '../shared/index.js';
import { useEventDraft } from '../../../context/useEventDraft.js';
import { useWorkQueueContext } from '../../../context/useWorkQueueContext.js';
import { mapClaimToTrainMeDetail } from '../../../utils/mapClaimToTrainMeDetail.js';
import { governanceActionErrorToast } from '../../../utils/governanceActionToast.js';
import { isDecisionSkipped } from '../../../utils/decisionSkipped.js';
import { ArrowUpCircle, XCircle } from 'lucide-react';

/**
 * Train Me decision for an interim `claim_suggestion` event (CT-5095 / EVT-011).
 *
 * Pixel-aligned with {@link TrainMeDecision}: Promote to Canon + Reject, promote
 * confirm modal, inline edit in the body. Backend mapping:
 *  - unchanged promote → claim-suggestion/accept
 *  - edited promote   → claim-suggestion/suggest (counter-suggestion)
 *  - reject           → claim-suggestion/decline
 *
 * Gaps vs prototype documented on CT-5095 (suggested_text, impact.agents, etc.).
 */
function ClaimSuggestionDecision({ event, actions }) {
  const { t } = useTranslation('work-queues-app');
  const toast = useToast();
  const { resolveClaimSuggestion, pendingDecisionIds } = useWorkQueueContext();
  const { draft, editing, cancelEdit } = useEventDraft();
  const trainMe = useMemo(() => mapClaimToTrainMeDetail(event), [event]);
  const isResolving = pendingDecisionIds?.has(event.id) ?? false;
  const [mode, setMode] = useState(null); // 'promote' | 'reject'

  const proposed = trainMe.proposedValue ?? '';
  const value = editing ? draft : proposed;

  const agentCount = trainMe.impact?.agents?.length ?? 0;
  const workflowCount = trainMe.impact?.workflows?.length ?? 0;

  const promoteRows = useMemo(
    () =>
      [
        { label: t('trainMe.field'), value: trainMe.fieldName },
        trainMe.currentValue
          ? { label: t('trainMe.change'), value: `${trainMe.currentValue} → ${value}` }
          : { label: t('trainMe.proposedValue'), value: value },
        trainMe.partition ? { label: t('trainMe.partition'), value: trainMe.partition } : null,
        agentCount > 0 ? { label: t('trainMe.agentsLabel'), value: String(agentCount) } : null,
        workflowCount > 0 ? { label: t('trainMe.workflowsLabel'), value: String(workflowCount) } : null,
      ].filter((row) => row?.value),
    [t, trainMe, value, agentCount, workflowCount],
  );

  const handleCloseMode = useCallback(() => setMode(null), []);

  const run = useCallback(
    async (params, copy, action) => {
      if (isResolving) return;
      try {
        await resolveClaimSuggestion(event.id, params);
        toast.success({ title: t(copy.title), message: t(copy.message) });
        handleCloseMode();
        if (action === 'suggest') cancelEdit();
      } catch (err) {
        if (isDecisionSkipped(err)) return;
        toast.error(governanceActionErrorToast(t, err, action));
        throw err;
      }
    },
    [event.id, handleCloseMode, isResolving, resolveClaimSuggestion, t, toast, cancelEdit],
  );

  const handlePromote = useCallback(
    ({ reason }) => {
      const text = (editing ? draft : proposed).trim();
      const baseline = (trainMe.baselineProposed ?? proposed).trim();
      if (text === baseline) {
        run(
          { outcome: 'promoted' },
          { title: 'trainMe.promotedTitle', message: 'trainMe.promotedMessage' },
          'promote',
        );
      } else {
        run(
          { outcome: 'suggested', suggestedText: text, reason },
          { title: 'claimSuggestion.suggestedTitle', message: 'claimSuggestion.suggestedMessage' },
          'suggest',
        );
      }
    },
    [draft, editing, proposed, run, trainMe.baselineProposed],
  );

  const handleReject = useCallback(
    ({ reason }) =>
      run(
        { outcome: 'rejected', reason },
        { title: 'trainMe.rejectedTitle', message: 'trainMe.rejectedMessage' },
        'decline',
      ),
    [run],
  );

  const primaryActions = useMemo(
    () => [
      {
        id: 'promote',
        label: isResolving ? t('governance.resolving') : t('trainMe.promote'),
        size: 'md',
        icon: ArrowUpCircle,
        disabled: isResolving,
        onClick: () => setMode('promote'),
      },
      {
        id: 'reject',
        label: t('trainMe.reject'),
        size: 'md',
        icon: XCircle,
        variant: 'danger',
        disabled: isResolving,
        onClick: () => setMode('reject'),
      },
    ],
    [isResolving, t],
  );

  return (
    <>
      <DecisionFooter
        event={event}
        primaryActions={primaryActions}
        onSkip={actions.skip}
        onAsk={actions.ask}
        onEscalate={actions.escalate}
        onTrace={actions.trace}
        extraMenuItems={isResolving ? [] : actions.extraMenuItems}
        sticky={actions.sticky}
      />

      <DecisionConfirmModal
        isOpen={mode === 'promote'}
        onClose={handleCloseMode}
        icon={<ArrowUpCircle size={20} className="text-emerald-500" />}
        breadcrumb={`${event.id} · ${event.refLabel ?? ''}`}
        title={t('trainMe.promoteTitle')}
        subtitle={t('trainMe.promoteSubtitle')}
        summaryRows={promoteRows}
        showReason
        reasonLabel={t('trainMe.noteLabel')}
        reasonPlaceholder={t('trainMe.notePlaceholder')}
        confirmLabel={t('trainMe.promote')}
        onConfirm={handlePromote}
      />

      <DecisionConfirmModal
        isOpen={mode === 'reject'}
        onClose={handleCloseMode}
        icon={<XCircle size={20} className="text-rose-500" />}
        breadcrumb={`${event.id} · ${event.refLabel ?? ''}`}
        title={t('trainMe.rejectTitle')}
        subtitle={t('trainMe.rejectSubtitle')}
        requireReason
        reasonLabel={t('trainMe.rejectReasonLabel')}
        reasonPlaceholder={t('trainMe.rejectReasonPlaceholder')}
        confirmLabel={t('trainMe.reject')}
        confirmVariant="danger"
        onConfirm={handleReject}
      />
    </>
  );
}

export default ClaimSuggestionDecision;
